var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { VirtualTable } from '@devexpress/dx-react-grid-material-ui';
import classNames from 'classnames';
import produce from 'immer';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { ImprovedDataTable } from '../Components/DataTable/ImprovedDataTable';
import { FieldIconMode } from '../Components/DatePicker';
import { FormikDatePicker } from '../Components/FormikFields/FormikDatePicker';
import { LayoutCentral } from '../Components/LayoutCentral';
import { MaskedTextField } from '../Components/MaskedTextField';
import { MedicationCard, MedicationCardMode } from '../Components/MedicationCard';
import { LoadingPlaceholder } from '../Components/Placeholder';
import { isSingleSelectSingleValue, StyledSelect } from '../Components/StyledSelect';
import { MedicationExpiry, MedicationType } from '../Contracts/MedicationModels';
import { medicationExpiryItems } from '../MedicationOverview/MedicationDetailsFields';
import { renderItemToMenuKey, renderItemToString, renderMedicationExpiryKey, renderMedicationExpiryName } from '../Shared/MedicationSharedMethods';
import { expiryDate, getDuration, getDurationType, performedDate, selectableDurationTypes } from '../MedicationSummary/MedicationSummaryDuration';
import { calculateCourseInstructions } from '../MedicationSummary/Utilities';
import { ValidDateFormats } from '../Shared/DateUtilities';
import { numberInputFilter } from '../Shared/InputFilters';
import { expiryStatusFromDate, isInactiveMedicationStatus } from '../Shared/MedicationsHelpers';
import { MedicationExpiryType } from '../Shared/Types';
import { backgroundAlt } from '../Styles/Colours';
import { isFreeTextMedication, ReprescribeFreeTextMedication } from './ReprescribeFreeTextMedication';
import { getExpiresOn, getExpiresOnByDuration } from './ReprescribeMedication';
import styles from './ReprescribeMedicationList.module.css';
import { displayError } from '../Config/ApplicationConfig';
import Tooltip from '@material-ui/core/Tooltip';
var getColumns = function (allMedications, props) {
    return [
        {
            name: 'product',
            title: 'Product',
            getCellValue: function (row) {
                return (React.createElement(MedicationCard, { name: row.overrideProductPack ? row.overrideProductPack.name : row.medication.name, strength: Number(row.medication.strength || '0'), strengthUnit: row.medication.strengthUnits, type: row.medication.type, selected: row.selected, size: row.medication.packAmount, productPackAmount: row.medication.productPackAmount, repeatsAmount: row.medication.repeatsAmount, mode: MedicationCardMode.Long, isNonStandardPackSize: row.medication.isNonStandardPackSize, nonStandardPackSize: row.medication.nonStandardPackSize }));
            }
        },
        {
            name: 'courseInstructions',
            title: 'Order instructions',
            getCellValue: function (row) {
                var instructions = calculateCourseInstructions({
                    orderInstructions: row.medication.orderInstruction,
                    dosageInstructions: row.medication.dosageInstruction,
                    type: row.medication.type
                });
                return (React.createElement(Tooltip, { title: instructions, placement: 'bottom-start' },
                    React.createElement("div", { className: classNames(styles.rowMainText, styles.wrappingCell) }, instructions)));
            }
        },
        {
            name: 'medicationExpiry',
            title: 'Expiry',
            getCellValue: function (row) {
                var index = allMedications.findIndex(function (it) { return it.medication.id === row.medication.id; });
                return (React.createElement("span", { className: styles.expiryPicker },
                    React.createElement(StyledSelect, { testId: 'medicationExpiry', readOnly: !allMedications[index].selected, collapseTitleLabel: true, hideWarningEmptyHelperText: true, itemToString: renderMedicationExpiryName, itemToMenuKey: renderMedicationExpiryKey, suggestions: medicationExpiryItems, maxResults: 0, isClearable: false, onSelection: function (selection) {
                            if (selection && isSingleSelectSingleValue(selection)) {
                                props.setFieldValue("medications[" + index + "].medicationExpiry", selection);
                            }
                            else {
                                console.log('Multi selection is not supported');
                            }
                        }, value: row.medicationExpiry })));
            }
        },
        {
            name: 'duration',
            title: 'Duration',
            getCellValue: function (row) {
                var _a;
                var chartDuration = ((_a = row.medicationExpiry) === null || _a === void 0 ? void 0 : _a.value) === MedicationExpiryType.Chart;
                if (chartDuration)
                    return React.createElement("span", { className: styles.durationPicker }, "(Chart duration)");
                var index = allMedications.findIndex(function (it) { return it.medication.id === row.medication.id; });
                return (React.createElement("span", { className: styles.durationPicker, onClick: function (e) {
                        e.stopPropagation();
                        e.preventDefault();
                    } },
                    React.createElement(MaskedTextField, { maxLength: 3, readOnly: !allMedications[index].selected, value: row.duration, label: null, filterFunction: numberInputFilter, hideCharacterLimit: true, hideWarningEmptyHelperText: true, fullWidth: true, onClear: function () {
                            props.setFieldValue("medications[" + index + "].expiresOn", '');
                            props.setFieldValue("medications[" + index + "].duration", '');
                        }, onChange: function (value) {
                            var enteredNumber = Number(value);
                            if (value === '') {
                                props.setFieldValue("medications[" + index + "].expiresOn", '');
                                props.setFieldValue("medications[" + index + "].duration", '');
                            }
                            else if (value && !isNaN(enteredNumber)) {
                                if (enteredNumber <= 999 && enteredNumber >= 1) {
                                    var expiresOn = getExpiresOnByDuration(performedDate(moment().endOf('day').format(ValidDateFormats.IsoFormat), ValidDateFormats.IsoFormat), value, row.durationType);
                                    props.setFieldValue("medications[" + index + "].expiresOn", expiresOn);
                                    props.setFieldValue("medications[" + index + "].duration", value);
                                }
                            }
                        }, testId: "text-input-duration-" + index }),
                    React.createElement(StyledSelect, { isClearable: false, maxResults: 3, collapseTitleLabel: true, hideWarningEmptyHelperText: true, value: row.durationType, itemToString: renderItemToString, itemToMenuKey: renderItemToMenuKey, disabled: !allMedications[index].selected, onSelection: function (newDurationType) {
                            if (newDurationType) {
                                var expiresOn = getExpiresOnByDuration(performedDate(moment().endOf('day').format(ValidDateFormats.IsoFormat), ValidDateFormats.IsoFormat), row.duration, newDurationType);
                                props.setFieldValue("medications[" + index + "].expiresOn", expiresOn);
                                props.setFieldValue("medications[" + index + "].durationType", newDurationType);
                            }
                        }, suggestions: selectableDurationTypes, testId: "selection-input-duration-type-" + index })));
            }
        },
        {
            name: 'until',
            title: 'End date',
            getCellValue: function (row) {
                var _a;
                var chartDuration = ((_a = row.medicationExpiry) === null || _a === void 0 ? void 0 : _a.value) === MedicationExpiryType.Chart;
                if (!chartDuration) {
                    var index_1 = allMedications.findIndex(function (it) { return it.medication.id === row.medication.id; });
                    return (React.createElement("span", { className: styles.untilCell, onClick: function (e) {
                            e.stopPropagation();
                            e.preventDefault();
                        } },
                        React.createElement(FormikDatePicker, { label: null, name: "medications[" + index_1 + "].expiresOn", placeholder: ValidDateFormats.Edit, readOnly: !allMedications[index_1].selected, calendarIconMode: FieldIconMode.Embed, fillContainer: true, isClearable: false, format: ValidDateFormats.IsoFormat, hideWarningEmptyHelperText: true, testId: "date-picker-until-" + index_1, onChange: function (date) {
                                if (date) {
                                    var newDurationType = getDurationType(moment(date, ValidDateFormats.IsoFormat).endOf('day'), performedDate(moment().endOf('day').format(ValidDateFormats.IsoFormat), ValidDateFormats.IsoFormat));
                                    var newDuration = getDuration(moment(date, ValidDateFormats.IsoFormat).endOf('day'), performedDate(moment().endOf('day').format(ValidDateFormats.IsoFormat), ValidDateFormats.IsoFormat), newDurationType);
                                    props.setFieldValue("medications[" + index_1 + "].durationType", newDurationType);
                                    props.setFieldValue("medications[" + index_1 + "].duration", newDuration);
                                }
                                else {
                                    props.setFieldValue("medications[" + index_1 + "].duration", '');
                                }
                            } })));
                }
            }
        }
    ];
};
var RowComponent = function (props) {
    var _a;
    var _b, _c, _d, _e, _f, _g;
    var selectByRowClick = props.selectByRowClick, highlighted = props.highlighted, onToggle = props.onToggle, otherProps = __rest(props, ["selectByRowClick", "highlighted", "onToggle"]);
    var expiryStatus = expiryStatusFromDate(props.tableRow.row.medication.expiresOn);
    var isInactive = isInactiveMedicationStatus(props.tableRow.row.medication.status);
    var rowStyles = classNames(styles.row, (_a = {},
        _a[styles.selectedRow] = highlighted,
        _a[styles.rowExpired] = !isInactive && expiryStatus === MedicationExpiry.Expired,
        _a[styles.rowAboutToExpire] = !isInactive && expiryStatus === MedicationExpiry.AboutToExpire,
        _a));
    var medCode = (_d = (_c = (_b = props.tableRow.row) === null || _b === void 0 ? void 0 : _b.medication) === null || _c === void 0 ? void 0 : _c.id) !== null && _d !== void 0 ? _d : '';
    var medName = ((_g = (_f = (_e = props.tableRow.row) === null || _e === void 0 ? void 0 : _e.medication) === null || _f === void 0 ? void 0 : _f.name) !== null && _g !== void 0 ? _g : '').substr(0, 20);
    return (React.createElement(VirtualTable.Row, __assign({}, otherProps, { "data-test-med-code": medCode, "data-test-med-name": medName, row: props.tableRow.row, className: rowStyles })));
};
var CustomCellMemo = function (props) {
    var _a;
    var cellStyles = classNames(styles.cell, (_a = {},
        _a[styles.interactive] = true,
        _a[styles.courseInstructions] = props.column.name === 'courseInstructions',
        _a));
    return React.createElement(VirtualTable.Cell, __assign({}, props, { className: cellStyles, "data-test-id": props.column.name }));
};
var columnExtensions = [
    {
        columnName: 'product'
    },
    {
        columnName: 'courseInstructions',
        width: 300
    },
    {
        columnName: 'medicationExpiry',
        width: 180
    },
    {
        columnName: 'duration',
        width: 200
    },
    {
        columnName: 'until',
        width: 160
    }
];
export var ReprescribeMedicationsList = function (props) {
    var selection = props.values.medications.filter(function (x) { return x.selected; }).map(function (x) { return x.medication.id; });
    useEffect(function () {
        props.values.medications.forEach(function (x, index) {
            // It is a selected item in the medication selection list.
            if (x.selected) {
                props.setFieldTouched("medications[" + index + "].expiresOn");
            }
        });
    }, []);
    var _a = __read(useState(null), 2), represcribeMedication = _a[0], setReprescribeMedication = _a[1];
    function updateState(medications, difference, selectedIds) {
        var newState = produce(medications, function (draft) {
            draft.forEach(function (x) {
                var foundInList = difference.findIndex(function (it) { return it === x.medication.id; });
                if (foundInList !== -1) {
                    // It is a selected item in the medication selection list.
                    var selected = selectedIds.findIndex(function (it) { return it === x.medication.id; }) !== -1;
                    var expiresOnMoment = expiryDate(x.medication.expiresOn);
                    var performedOnMoment = performedDate(x.medication.performedOn, ValidDateFormats.IsoFormat);
                    var durationType = getDurationType(expiresOnMoment, performedOnMoment);
                    if (!selected && x.medication.isFreeTextMedication) {
                        x.overrideProductPack = null;
                        x.overrideMedicationType = null;
                    }
                    x.duration = getDuration(expiresOnMoment, performedOnMoment, durationType);
                    x.expiresOn = getExpiresOn(selected, performedDate(moment().endOf('day').format(ValidDateFormats.IsoFormat), ValidDateFormats.IsoFormat), expiresOnMoment, performedOnMoment, durationType);
                    x.durationType = durationType;
                    x.selected = selected;
                }
            });
        });
        var newTouchedState = newState.map(function (x) {
            if (x.selected) {
                return { expiresOn: true };
            }
            else {
                return { expiresOn: false };
            }
        });
        props.setValues({ medications: newState });
        props.setTouched({ medications: newTouchedState });
    }
    var onTableDataSelectionChanged = function (selectedIds, selectAllClicked) {
        var _a;
        // Get all the difference (selected and unselected); between the original and new medication selection list.
        var difference = selection
            .filter(function (x) { return !selectedIds.includes(x); })
            .concat(selectedIds.filter(function (x) { return !selection.includes(x); }));
        var selectedIdsNoFreeText;
        var freeTextMedicationPresent = false;
        if (selectAllClicked && selectedIds.length > 0) {
            selectedIdsNoFreeText = selectedIds.filter(function (value) {
                var _a;
                var medication = (_a = props.values.medications.find(function (it) { return it.medication.id === value; })) !== null && _a !== void 0 ? _a : null;
                return !((medication === null || medication === void 0 ? void 0 : medication.medication.type) === MedicationType.MedicationHistory &&
                    (medication === null || medication === void 0 ? void 0 : medication.medication.isFreeTextMedication));
            });
            freeTextMedicationPresent = selectedIdsNoFreeText.length < selectedIds.length;
        }
        else {
            selectedIdsNoFreeText = selectedIds;
        }
        if (selectAllClicked) {
            // if select all, select selectedIdsNoFreeText meds
            updateState(props.values.medications, difference, selectedIdsNoFreeText);
            if (freeTextMedicationPresent) {
                // if there were free text items, toast
                displayError('Free text medications cannot be selected using select all. Please select these individually.');
            }
        }
        else {
            // free text triggers dialog, not free text is select/deselect
            var changedMedication_1 = (_a = props.values.medications.find(function (it) { return it.medication.id === difference[0]; })) !== null && _a !== void 0 ? _a : null;
            if (changedMedication_1 &&
                changedMedication_1.medication.type === MedicationType.MedicationHistory &&
                changedMedication_1.medication.isFreeTextMedication) {
                if (changedMedication_1.selected) {
                    // Run normal selection logic, deselecting it.
                    updateState(props.values.medications, difference, selectedIds);
                }
                else {
                    if (selectAllClicked && freeTextMedicationPresent) {
                        updateState(props.values.medications, difference, selectedIdsNoFreeText);
                        displayError('Free text medications cannot be selected using select all. Please select these individually.');
                    }
                    else {
                        // Kick off the represcribe modal flow.
                        setReprescribeMedication({
                            patientMedication: changedMedication_1.medication,
                            text: changedMedication_1.medication.name,
                            onClose: function () {
                                // On close, just do nothing
                                setReprescribeMedication(null);
                            },
                            onSelectMedication: function (config) {
                                // On selection of a medication, update the target medication override values accordingly.
                                setReprescribeMedication(null);
                                var meds = props.values.medications.map(function (med) {
                                    var _a;
                                    if (med.medication.id === changedMedication_1.medication.id) {
                                        var expiresOnMoment = expiryDate(med.expiresOn);
                                        var performedOnMoment = performedDate(med.medication.performedOn, ValidDateFormats.IsoFormat);
                                        var durationType = getDurationType(expiresOnMoment, performedOnMoment);
                                        console.log(config.patientMedication.expiryType);
                                        var updatedMedicationState = __assign(__assign({}, med), { duration: getDuration(expiresOnMoment, performedOnMoment, durationType), expiresOn: getExpiresOn(true, performedDate(moment().endOf('day').format(ValidDateFormats.IsoFormat), ValidDateFormats.IsoFormat), expiresOnMoment, performedOnMoment, durationType), medicationExpiry: { value: config.patientMedication.expiryType }, overrideMedicationType: config.type, overrideProductPack: config.medication, overridePbsDetail: isFreeTextMedication(config.medication)
                                                ? null
                                                : (_a = config.medication.pbsDetail) !== null && _a !== void 0 ? _a : null });
                                        return updatedMedicationState;
                                    }
                                    return med;
                                });
                                updateState(meds, difference, selectedIds);
                            }
                        });
                    }
                }
            }
            else {
                updateState(props.values.medications, difference, selectedIds);
            }
        }
    };
    if (props.values.medications != null && props.values.medications.length > 0) {
        return (React.createElement("div", { className: styles.tableContainer },
            React.createElement(ImprovedDataTable, { headerColour: backgroundAlt, columns: getColumns(props.values.medications, props), data: props.values.medications, cellComponent: CustomCellMemo, columnExtensions: columnExtensions, getRowId: function (item) { return item.medication.id; }, rowChecking: {
                    selectedIds: selection,
                    onSelectedChanged: onTableDataSelectionChanged,
                    showSelectAll: true,
                    showSelectionColumn: true,
                    rowComponent: RowComponent
                } }),
            represcribeMedication && (React.createElement(ReprescribeFreeTextMedication, { patientMedication: represcribeMedication.patientMedication, freeText: represcribeMedication.text, onSelectionMedication: represcribeMedication.onSelectMedication, onClose: represcribeMedication.onClose }))));
    }
    else {
        return (React.createElement(LayoutCentral, null,
            React.createElement(LoadingPlaceholder, null)));
    }
};
